import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DatePicker } from '../components/ui/date-picker';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Trash2, Calendar, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const currentYear = new Date().getFullYear();

export default function PublicHolidaysManager({ api }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(currentYear);
  const [country, setCountry] = useState('');
  const [defaultRegion, setDefaultRegion] = useState('');
  const [availableRegions, setAvailableRegions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ date: '', name: '', region: '' });

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/public-holidays?year=${year}`);
      setHolidays(res.data.holidays || []);
      setCountry(res.data.country || '');
      setDefaultRegion(res.data.default_region || '');
      setAvailableRegions(res.data.available_regions || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api, year]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const handleAddCustom = async () => {
    if (!form.date || !form.name.trim()) {
      toast.error('Date and name are required');
      return;
    }
    try {
      await api.post('/public-holidays', {
        date: form.date,
        name: form.name,
        region: form.region || null,
      });
      toast.success('Custom holiday added');
      setDialogOpen(false);
      setForm({ date: '', name: '', region: '' });
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/public-holidays/${deleteTarget.id}`);
      toast.success('Holiday removed');
      setDeleteTarget(null);
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRegionChange = async (region) => {
    try {
      await api.put(`/public-holidays/default-region?region=${encodeURIComponent(region)}`);
      setDefaultRegion(region);
      toast.success(`Default region set to ${region}`);
    } catch (err) {
      toast.error('Failed to update region');
    }
  };

  // Filter to show only national + matching region holidays
  const visibleHolidays = holidays.filter(h =>
    h.national || !h.region || h.region === defaultRegion || h.source === 'custom'
  );

  const countryLabel = { AU: 'Australia', NZ: 'New Zealand', US: 'United States', GB: 'United Kingdom' }[country] || country;

  return (
    <Card className="card-industrial" data-testid="public-holidays-manager">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#ffd108]" />
              Public Holidays
            </CardTitle>
            <CardDescription>
              {countryLabel} public holidays for payroll overtime rules. Dates matching your region are auto-detected.
            </CardDescription>
          </div>
          <Button
            onClick={() => { setForm({ date: `${year}-01-01`, name: '', region: '' }); setDialogOpen(true); }}
            className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            data-testid="add-custom-holiday-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Custom Holiday
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Year</Label>
            <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
              <SelectTrigger className="w-28" data-testid="holiday-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[currentYear, currentYear + 1, currentYear + 2].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {availableRegions.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Your Region
              </Label>
              <Select value={defaultRegion} onValueChange={handleRegionChange}>
                <SelectTrigger className="w-40" data-testid="holiday-region-select">
                  <SelectValue placeholder="Select region..." />
                </SelectTrigger>
                <SelectContent>
                  {availableRegions.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
            <Globe className="h-4 w-4" />
            {countryLabel} ({visibleHolidays.length} holidays)
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-muted-foreground py-8 text-center">Loading holidays...</div>
        ) : visibleHolidays.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center">No holidays found for {year}.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Holiday</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleHolidays.map((h) => (
                  <TableRow key={h.id || h.date + h.name} data-testid={`holiday-row-${h.date}`}>
                    <TableCell className="font-mono text-sm">{h.date}</TableCell>
                    <TableCell className="font-medium">{h.name}</TableCell>
                    <TableCell>
                      {h.national ? (
                        <Badge variant="outline" className="text-xs">National</Badge>
                      ) : h.region ? (
                        <Badge variant="outline" className="text-xs border-[#ffd108]/40 text-[#ffd108]">{h.region}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs ${h.source === 'custom' ? 'text-[#ffd108]' : 'text-muted-foreground'}`}>
                        {h.source === 'custom' ? 'Custom' : 'System'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {h.source === 'custom' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 h-8 w-8"
                          onClick={() => setDeleteTarget(h)}
                          data-testid={`delete-holiday-${h.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Add Custom Holiday Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Date <span className="text-red-500">*</span></Label>
              <DatePicker
                value={form.date}
                onChange={(d) => setForm({ ...form, date: d })}
                testId="custom-holiday-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Holiday Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Company Shutdown Day"
                data-testid="custom-holiday-name"
              />
            </div>
            {availableRegions.length > 0 && (
              <div className="space-y-1.5">
                <Label>Region (optional — leave blank for all)</Label>
                <Select value={form.region || '_all'} onValueChange={v => setForm({ ...form, region: v === '_all' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All regions (national)</SelectItem>
                    {availableRegions.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCustom} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="save-custom-holiday-btn">
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Holiday</AlertDialogTitle>
            <AlertDialogDescription>
              Remove "{deleteTarget?.name}" on {deleteTarget?.date}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
